import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const VERDE = "#0d9488";
const FUSO = "America/Sao_Paulo";

const diaDe = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", { timeZone: FUSO }); // AAAA-MM-DD
const rotuloDia = (iso) => {
  const t = new Date(iso).toLocaleDateString("pt-BR", {
    timeZone: FUSO,
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
  return t.charAt(0).toUpperCase() + t.slice(1).replace(".", "");
};
const hora = (iso) =>
  new Date(iso).toLocaleTimeString("pt-BR", { timeZone: FUSO, hour: "2-digit", minute: "2-digit" });

const chip = (ativo) => ({
  border: ativo ? `2px solid ${VERDE}` : "1px solid #d5e6e3",
  background: ativo ? "rgba(13,148,136,.1)" : "#fff",
  color: ativo ? "#0f766e" : "#334155",
  fontWeight: 700,
  fontSize: "0.82rem",
  borderRadius: 10,
  padding: "8px 12px",
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
});

/**
 * O RH marca a entrevista do candidato direto pela conversa: escolhe o dia e
 * o horario entre os livres da Agenda. Opcionalmente manda a confirmacao (e a
 * localizacao) para o candidato no WhatsApp.
 */
const AgendarEntrevistaModal = ({ ticketId, aberto, onClose, container }) => {
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [livres, setLivres] = useState([]);
  const [vagas, setVagas] = useState([]);
  const [dia, setDia] = useState("");
  const [slotId, setSlotId] = useState(null);
  const [nome, setNome] = useState("");
  const [vaga, setVaga] = useState("");
  const [obs, setObs] = useState("");
  const [enviar, setEnviar] = useState(true);

  useEffect(() => {
    if (!aberto || !ticketId) return;
    let vivo = true;
    setCarregando(true);
    setDia("");
    setSlotId(null);
    setObs("");
    Promise.all([
      api.get("/interviews/livres"),
      api.get(`/tickets/${ticketId}`),
      api.get("/interviews"),
    ])
      .then(([l, t, todos]) => {
        if (!vivo) return;
        setLivres(l.data || []);
        const nomeWhats = String(t.data?.contact?.name || "");
        setNome(/^[\d\s+()-]+$/.test(nomeWhats) ? "" : nomeWhats);
        const nomes = Array.from(
          new Set((todos.data || []).map((s) => String(s.jobTitle || "").trim()).filter(Boolean))
        );
        setVagas(nomes);
        setVaga("");
      })
      .catch((err) => toastError(err))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [aberto, ticketId]);

  const porDia = useMemo(() => {
    const m = new Map();
    livres.forEach((s) => {
      const d = diaDe(s.startAt);
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(s);
    });
    return m;
  }, [livres]);
  const dias = Array.from(porDia.keys());
  const doDia = dia ? porDia.get(dia) || [] : [];

  const agendar = async () => {
    if (!slotId) {
      toast.info("Escolha o dia e o horário.");
      return;
    }
    setSalvando(true);
    try {
      const { data } = await api.post(`/interviews/${slotId}/agendar-conversa`, {
        ticketId,
        nome,
        vaga,
        observacoes: obs,
        enviarConfirmacao: enviar,
      });
      if (data?.aviso) toast.warn(data.aviso);
      else toast.success(enviar ? "Entrevista agendada e confirmação enviada." : "Entrevista agendada.");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error;
      if (msg === "ERR_SLOT_UNAVAILABLE") toast.error("Esse horário acabou de ser ocupado. Escolha outro.");
      else toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={onClose} maxWidth="sm" fullWidth container={container}>
      <DialogTitle>Agendar entrevista</DialogTitle>
      <DialogContent dividers>
        {carregando ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={26} />
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <TextField
                label="Nome do candidato"
                variant="outlined"
                size="small"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
              <Autocomplete
                freeSolo
                options={vagas}
                inputValue={vaga}
                onInputChange={(e, v) => setVaga(v || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Vaga" variant="outlined" size="small" />
                )}
              />
            </div>

            <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>
              1. Escolha o dia
            </div>
            {!dias.length ? (
              <div style={{ fontSize: "0.85rem", color: "#b45309", marginBottom: 12 }}>
                Não há horários livres na Agenda. Cadastre horários na tela Agenda.
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                {dias.map((d) => (
                  <button
                    type="button"
                    key={d}
                    style={chip(dia === d)}
                    onClick={() => {
                      setDia(d);
                      setSlotId(null);
                    }}
                  >
                    {rotuloDia(porDia.get(d)[0].startAt)}
                    <span style={{ fontWeight: 600, opacity: 0.65, marginLeft: 6 }}>
                      ({porDia.get(d).length})
                    </span>
                  </button>
                ))}
              </div>
            )}

            {dia && (
              <>
                <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>
                  2. Escolha o horário
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                  {doDia.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      style={chip(slotId === s.id)}
                      onClick={() => setSlotId(s.id)}
                      title={s.title || ""}
                    >
                      {hora(s.startAt)}
                    </button>
                  ))}
                </div>
              </>
            )}

            <TextField
              label="Observações para o RH (opcional)"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={2}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
            />
            <FormControlLabel
              style={{ marginTop: 8 }}
              control={<Checkbox color="primary" checked={enviar} onChange={(e) => setEnviar(e.target.checked)} />}
              label="Enviar a confirmação para o candidato no WhatsApp (com a localização)"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          onClick={agendar}
          color="primary"
          variant="contained"
          disabled={salvando || !slotId}
          style={{ textTransform: "none", fontWeight: 800 }}
        >
          {salvando ? "Agendando..." : "Agendar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgendarEntrevistaModal;
